<template>
  <ValidationObserver
    ref="flowNodeObserver"
    v-slot="{ valid }" >
    <b-form-group>
      <label
        for=""
        class="settings__label-sub" >
        This trigger will activate when
      </label>
      <ValidationProvider
        v-slot="{ errors }"
        rules="required">
        <multiselect
          v-model="whenWillItTrigger"
          placeholder="Select an option"
          class="flex-options hide-tags no-selected-background"
          :options="whenOptions"
          label="text"
          track-by="value"
          :multiple="false"
          :show-labels="false"
          :show-no-results="false"
          :close-on-select="true"
          :clear-on-select="false"
          :preserve-search="true"
          :allow-empty="false"
          :preselect-first="true"
          :searchable="false" >
          <template
            slot="option"
            slot-scope="props">
            <SBIcon
              :icon="SBIconList.DONE_TICK"
              :color="SBColors.bluePrimary"
              :size="9"/>
            {{ props.option.text }}
          </template>
        </multiselect>
      </ValidationProvider>
    </b-form-group>
    <b-form-group
      v-if="whenWillItTrigger && [BlockSelectTypes.KEYWORD, BlockSelectTypes.KEYWORD_SINGLE, BlockSelectTypes.KEYWORD_CONDITION].includes(triggerType)">
      <label
        for=""
        class="settings__label-sub" >
        {{ triggerType === BlockSelectTypes.KEYWORD_CONDITION ? 'Keywords' : 'Keyword' }}
      </label>
      <ValidationProvider
        v-slot="{ errors }"
        rules="required|validChild">
        <TagInput
          :ref="`extendRef`"
          v-model="additionalData[whenWillItTrigger.value]"
          :condition="additionalData[whenWillItTrigger.value].condition"
          :show-condition="triggerType === BlockSelectTypes.KEYWORD_CONDITION"
          :single-tag="triggerType === BlockSelectTypes.KEYWORD_SINGLE"/>
        <div
          v-if="additionalData[whenWillItTrigger.value].changed && errors[0]"
          class="error flex flex-align-center">
          <SBIcon
            class="mr-2"
            :icon="SBIconList.ERROR"
            :color="SBColors.redPrimary"
            :size="12" />
          <span>Required field</span>
        </div>
      </ValidationProvider>
    </b-form-group>
  </ValidationObserver>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import SBColors from '@/helpers/lists/colors';
import BlockSelectTypes from '@/helpers/lists/BlockSelectTypes';
import TagInput from '@/components/common/TagInput';

export default {
  name: 'FlowTriggerDetails',
  components: {
    Multiselect,
    SBIcon,
    TagInput,
  },
  props: {
    node: {
      type: Object,
      required: true,
    },
    submit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      whenWillItTrigger: null,
      additionalData: {},
      SBIconList,
      SBColors,
      BlockSelectTypes,
    };
  },
  computed: {
    ...mapGetters({
      flowsDefinitionsUI: 'flows/flowsDefinitionsUI',
      flowData: 'flows/flowData',
      flowsDefinitions: 'flows/flowsDefinitions',
    }),
    whenOptions() {
      return Object.values(this.flowsDefinitionsUI.triggers[this.node.typeSubcode].options).map(option => ({
        value: option.type,
        text: option.name,
        icon: option.icon,
      }));
    },
    triggerType() {
      switch (this.whenWillItTrigger.value) {
        case 'full_match':
          return BlockSelectTypes.KEYWORD_SINGLE;
        case 'contains_keywords':
          return BlockSelectTypes.KEYWORD_CONDITION;
        default:
          return BlockSelectTypes.OPTION;
      }
    },
  },
  watch: {
    submit() {
      if (this.submit) {
        this.submitAction();
      }
    },
    whenWillItTrigger: {
      handler() {
        if (this.whenWillItTrigger && this.additionalData[this.whenWillItTrigger.value]) {
          this.$set(this.additionalData, this.whenWillItTrigger.value, { ...this.additionalData[this.whenWillItTrigger.value] });
        } else if (this.whenWillItTrigger && !this.additionalData[this.whenWillItTrigger.value]) {
          this.$set(this.additionalData, this.whenWillItTrigger.value, { tags: [] });
        }

        if (this.$refs.flowNodeObserver) {
          this.$refs.flowNodeObserver.reset();
          this.$nextTick(() => {
            this.$refs.flowNodeObserver.reset();
          });
        }
      },
      immediate: true,
    },
  },
  mounted() {    
    if (this.node.blockData.params && this.node.blockData.params.triggers && this.node.blockData.params.triggers[0] && this.node.blockData.params.triggers[0].type && [BlockSelectTypes.KEYWORD, BlockSelectTypes.KEYWORD_SINGLE, BlockSelectTypes.KEYWORD_CONDITION].includes(this.myTriggerType(this.node.blockData.params.triggers[0].type))) {
      this.whenWillItTrigger = this.whenOptions.find(option => option.value === this.node.blockData.params.triggers[0].type);
      if (Array.isArray(this.node.blockData.params.triggers[0].value)) {
        this.$set(this.additionalData, this.whenWillItTrigger.value, { tags: this.node.blockData.params.triggers[0].value.map(t => ({ tag: t })) });
      } else {
        this.$set(this.additionalData, this.whenWillItTrigger.value, { tags: [{ tag: this.node.blockData.params.triggers[0].value }] });
      }
    }
  },
  methods: {
    ...mapActions({
      saveFlowData: 'flows/saveFlowData',
      updateFlow: 'flows/updateFlow',
      updateFlowData: 'flows/updateFlowData',
    }),
    submitAction() {
      this.$refs.flowNodeObserver.validate()
        .then((valid) => {
          if (!valid) {
            return;
          }
          const targetTypeDefinition = this.flowsDefinitions.blocks.definitions[this.node.blockData.category].find(l => l.meta.type === this.node.blockData.type);
          const submitFlow = JSON.parse(JSON.stringify(this.flowData));
          submitFlow.blocks[0].params.triggers[0].type = this.whenWillItTrigger.value;

          if (this.triggerType === BlockSelectTypes.KEYWORD_SINGLE || this.triggerType === BlockSelectTypes.KEYWORD_CONDITION) {
            submitFlow.blocks[0].params.triggers[0].value = this.additionalData[this.whenWillItTrigger.value].tags.map(tag => tag.tag);
          } else {
            delete submitFlow.blocks[0].params.triggers[0].value;
            delete submitFlow.blocks[0].params.triggers[0].operator;
          }

          if (this.triggerType === BlockSelectTypes.KEYWORD_CONDITION) {
            submitFlow.blocks[0].params.triggers[0].operator = {
              value: this.additionalData[this.whenWillItTrigger.value].condition,
            };
          }
          try {
            const targetTrigger = targetTypeDefinition.definition.triggers.find(t => t.type === submitFlow.blocks[0].params.triggers[0].type);
            switch (targetTrigger.value_type) {
              case 'str':
                submitFlow.blocks[0].params.triggers[0].value = submitFlow.blocks[0].params.triggers[0].value[0];
                break;
              default:
                break;
            }
          } catch (error) {}

          this.updateFlow({
            numberId: this.flowData.business_uid,
            flowId: this.flowData.flow_id,
            blocks: submitFlow.blocks,
          })
            .then((data) => {
              this.updateFlowData(data.data.flow_data);
              this.$emit('submitted');
            })
            .catch((error) => {
              this.$emit('error', error);
            });
        });
    },
    myTriggerType(type) {
      switch (type) {
        case 'full_match':
          return BlockSelectTypes.KEYWORD_SINGLE;
        case 'contains_keywords':
          return BlockSelectTypes.KEYWORD_CONDITION;
        default:
          return BlockSelectTypes.OPTION;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
</style>
